// Third Party Imports
import _ from "lodash";

// Models & Constants
import { COUNTRY_CURRENCY_LIST, CountryCurrency } from "@/app/interface/currency";

export const setCurrencySign = (amount: number, currency: string): string => {
    return `${currency}${amount}`;
};

export const formatAmountToK = (num: number, currency: string) => {
    const val = Math.abs(num);

    if (val >= 1_00_00_000) {
        // 1 crore = 10,000,000
        return currency + (val / 1_00_00_000).toFixed(2).replace(/\.?0+$/, "") + "Cr";
    } else if (val >= 1_00_000) {
        // 1 lakh = 100,000
        return currency + (val / 1_00_000).toFixed(2).replace(/\.?0+$/, "") + "L";
    } else if (val >= 1_000) {
        // thousand
        return currency + (val / 1_000).toFixed(1).replace(/\.0$/, "") + (val > 9999 ? "K" : "k");
    }

    return currency + val.toFixed(2);
};

export const formatAmountToUS = (num: number, currency: string) => {
    const sign = num < 0 ? "-" : "";
    const val = Math.abs(num);

    if (val >= 1_000_000_000_000) {
        // Trillion
        return sign + currency + (val / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, "") + "T";
    } else if (val >= 1_000_000_000) {
        // Billion
        return sign + currency + (val / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "") + "B";
    } else if (val >= 1_000_000) {
        // Million
        return sign + currency + (val / 1_000_000).toFixed(2).replace(/\.?0+$/, "") + "M";
    } else if (val >= 1_000) {
        // Thousand
        return sign + currency + (val / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }

    return sign + currency + val.toFixed(2);
};

export function formatCurrency(value: number, currencyCode: string, locale: string): string {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
    }).format(value);
}

export function getCultureDetails(currencyCode: string, locale: string): CountryCurrency {
    const result = _.find(COUNTRY_CURRENCY_LIST, { currencyCode, locale }) as CountryCurrency;
    return result;
}

export function getPureAmount(amount: string) {
    if (!amount) return 0;

    // Remove currency symbol and commas, trim spaces
    const cleaned = amount.replace(/[\D+,]/g, "").trim();

    // Extract numeric part and suffix
    const match = cleaned.match(/^(-?\d+(\.\d+)?)([KMBkmb])?$/);

    if (!match) return NaN;

    let value = parseFloat(match[1]);
    const suffix = match[3] ? match[3].toUpperCase() : "";

    const multipliers: Record<string, number> = {
        K: 1_000,
        M: 1_000_000,
        B: 1_000_000_000,
    };

    if (suffix && multipliers[suffix]) {
        value *= multipliers[suffix];
    }

    return value;
}

export function parseAmount(amount: string) {
    const multipliers = {
        k: 1e3,
        K: 1e3,
        M: 1e6,
        B: 1e9,
        T: 1e12,
    };

    // Remove $ and commas
    const cleaned = amount.replace(/[$,]/g, "").trim();

    // Match number + optional suffix
    const match = cleaned.match(/^([+-]?[\d.]+)([kKMBT]?)$/);

    if (!match) {
        throw new Error("Invalid amount format");
    }

    const value = parseFloat(match[1]);
    const suffix = match[2] as keyof typeof multipliers;

    return value * (multipliers[suffix] || 1);
}

export function formatAmount(value: number): string {
    const abs = Math.abs(value);

    if (abs >= 1e12) {
        return `${(value / 1e12).toFixed(2)}T`;
    }

    if (abs >= 1e9) {
        return `${(value / 1e9).toFixed(2)}B`;
    }

    if (abs >= 1e6) {
        return `${(value / 1e6).toFixed(2)}M`;
    }

    if (abs >= 1e3) {
        return `${(value / 1e3).toFixed(2)}k`;
    }

    return value.toString();
}

export function formatAmountWithDecimal(amount: string): string {
    return amount.replace(/^([+-]?\$?\d+)([kKMBT])$/, (_, value, suffix) => `${value}.0${suffix}`);
}
